import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import { FUNDER_NAV } from "./FunderTerminal";
import { Check, Sparkles, Loader2, Receipt } from "lucide-react";

const _STATUS_PILL_BG = {
  active: "bg-[#0A8B85]/10 text-[#0A8B85]",
  trial:  "bg-amber-50 text-amber-800",
};
const _statusPillCls = (state) =>
  _STATUS_PILL_BG[state] || "bg-red-50 text-red-700";

const _TX_LABEL = {
  founder_readiness_credits: () => "Founder readiness credits",
  service_request: (t) => `Service · ${t.service_key || ""}`,
};
const _txLabel = (t) =>
  (_TX_LABEL[t.kind] || ((row) => `Subscription · ${row.plan_id || "—"}`))(t);

const _PAY_PILL_BG = {
  paid:    "bg-emerald-500/15 text-emerald-700",
  pending: "bg-amber-500/15 text-amber-700",
};
const _payPillCls = (status) =>
  _PAY_PILL_BG[status] || "bg-neutral-200 text-neutral-600";

export default function FunderBilling() {
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();

  const load = useCallback(async () => {
    const [p, s, t] = await Promise.all([
      api.get("/billing/plans"),
      api.get("/billing/status"),
      api.get("/billing/transactions").catch(() => ({ data: { transactions: [] } })),
    ]);
    setPlans(p.data);
    setStatus(s.data);
    setTransactions(t.data.transactions || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Poll if returning from Paystack (uses `reference` / `trxref` params).
  useEffect(() => {
    const sid = params.get("reference") || params.get("trxref") || params.get("session_id");
    if (params.get("canceled")) {
      setMsg("Payment canceled.");
      params.delete("canceled"); setParams(params);
      return;
    }
    if (!sid) return;
    let cancelled = false;
    setBusy(true);
    setMsg("Checking payment…");
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.post(`/billing/poll/${sid}`);
        if (data.payment_status === "paid") {
          setMsg("Payment successful — subscription extended by 30 days.");
          setBusy(false);
          ["reference","trxref","session_id"].forEach(k => params.delete(k));
          setParams(params);
          await load();
          return;
        }
        if (data.payment_status === "failed" || data.status === "expired") {
          setMsg("Payment was not completed. You can try again."); setBusy(false);
          ["reference","trxref","session_id"].forEach(k => params.delete(k));
          setParams(params);
          return;
        }
        if (++attempts < 8) setTimeout(poll, 2000);
        else { setMsg("Still processing. Please check back shortly."); setBusy(false); }
      } catch (err) {
        if (process.env.NODE_ENV === "development") console.warn("[FunderBilling] poll failed", err);
        if (++attempts < 8) setTimeout(poll, 2000);
        else { setMsg("Could not verify payment. Try again."); setBusy(false); }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [params.get("reference"), params.get("trxref"), params.get("session_id"), params.get("canceled")]);

  const checkout = async (plan_id) => {
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setMsg(e.response?.data?.detail || "Checkout failed.");
      setBusy(false);
    }
  };

  return (
    <AppShell nav={FUNDER_NAV}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900">Billing & plans</h1>
          <p className="mt-1 text-sm text-neutral-600">Choose your plan after the 90-day free trial. Cancel anytime.</p>
        </div>
        {status && (
          <div className={"rounded-xl px-4 py-3 text-sm " + _statusPillCls(status.state)}
               data-testid="billing-status-pill">
            {status.state === "active" && <span><b>Active</b> · {status.plan} · {status.days_left} days remaining</span>}
            {status.state === "trial" && <span><b>Free trial</b> · {status.days_left} days remaining</span>}
            {status.state === "expired" && <span><b>Trial ended</b> · upgrade to keep access</span>}
          </div>
        )}
      </div>

      {msg && <p className="mt-4 rounded-lg bg-[#F4F8F8] px-4 py-2 text-sm text-neutral-700" data-testid="billing-msg">{busy && <Loader2 className="mr-2 inline h-3 w-3 animate-spin"/>}{msg}</p>}

      <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
        {plans.map(p => (
          <div key={p.id} data-testid={`plan-${p.id}`}
            className={"relative rounded-2xl border bg-white p-7 " +
              (p.popular ? "border-[#0A8B85] shadow-[0_30px_60px_-30px_rgba(10,139,133,0.45)] ring-1 ring-[#0A8B85]" : "border-neutral-200")}>
            {p.popular && (
              <span className="absolute -top-3 left-7 inline-flex items-center gap-1 rounded-full bg-[#0A8B85] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                <Sparkles className="h-3 w-3"/> Most popular
              </span>
            )}
            <p className="text-sm font-bold uppercase tracking-wider text-[#0A8B85]">{p.name}</p>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="text-4xl font-extrabold text-neutral-900">${p.amount.toLocaleString()}</span>
              <span className="text-sm text-neutral-500">/ {p.interval}</span>
            </div>
            <p className="mt-2 text-sm text-neutral-600">{p.blurb}</p>
            <ul className="mt-5 space-y-2">
              {(p.features||[]).map(f => (
                <li key={f} className="flex items-start gap-2 text-sm text-neutral-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#0A8B85]"/>{f}
                </li>
              ))}
            </ul>
            <button data-testid={`subscribe-${p.id}`} disabled={busy} onClick={()=>checkout(p.id)}
              className={"mt-7 w-full rounded-lg py-2.5 text-sm font-semibold transition-colors disabled:opacity-50 " +
                (p.popular ? "bg-[#0A8B85] text-white hover:bg-[#066667]"
                           : "border border-neutral-200 bg-white text-neutral-900 hover:bg-neutral-50")}>
              {status?.state === "active" && status.plan === p.id ? "Current plan · Extend +30d" : "Subscribe"}
            </button>
          </div>
        ))}
      </div>

      <p className="mt-8 text-xs text-neutral-500">
        Payments are processed securely by Paystack. Each successful payment extends your subscription by 30 days.
      </p>

      {transactions.length > 0 && (
        <section className="mt-10" data-testid="billing-tx-history">
          <div className="flex items-center gap-2">
            <Receipt className="h-4 w-4 text-[#0A8B85]" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-700">Payment history</h2>
          </div>
          <div className="mt-3 overflow-hidden rounded-xl border border-neutral-200 bg-white">
            <table className="w-full text-left text-sm">
              <thead className="bg-neutral-50 text-[11px] font-bold uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Item</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {transactions.map((t) => (
                  <tr key={t.tx_id} data-testid={`billing-tx-${t.tx_id}`}>
                    <td className="px-4 py-3 text-neutral-600">{(t.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 text-neutral-800">{_txLabel(t)}</td>
                    <td className="px-4 py-3 text-neutral-800">${Number(t.amount || 0).toLocaleString()} <span className="text-xs text-neutral-500">{(t.currency || "usd").toUpperCase()}</span></td>
                    <td className="px-4 py-3">
                      <span className={
                        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase " +
                        _payPillCls(t.payment_status)}>
                        {t.payment_status || t.status || "—"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <button onClick={()=>nav("/funder/terminal")} className="mt-4 text-xs font-semibold text-[#0A8B85] hover:underline">← Back to Terminal</button>
    </AppShell>
  );
}
